<template>
  <div :class="{'ruler-container': true, 'ruler-horizontal': isHorizontal, 'ruler-vertical': isVertical}" ref="canvasContainer">
    <canvas ref="rulerCanvas" class="ruler-canvas" />
  </div>
</template>

<style lang="scss" scoped>
$ruler-size: 18px;
$ruler-border: 1px solid #808080;

.ruler-horizontal {
  flex: 1;
  height: $ruler-size;
  background-color: #ffffff;
  border-bottom: $ruler-border;
  margin-left: -1px;
}
.ruler-vertical {
  width: $ruler-size;
  height: 100%;
  background-color: #ffffff;
  border-right: $ruler-border;
  margin-top: -1px;
}
.ruler-container {
  position: relative;
}
.ruler-canvas {
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
</style>

<script>
export default {
  name: 'Ruler',
  props: {
    orientation: String,
    visible: Boolean,
    showMousePosition: Boolean,
    // Scale: pixels per mm
    scale: {
      type: Number,
      default: 1.0
    },
    // originOffset: pixels across where the origin starts
    originOffset: {
      type: Number,
      default: 0.0
    },
    mousePosition: {
      type: Number,
      default: 0.0
    },
    theme: Object
  },
  computed: {
    isHorizontal() {
      return this.$props.orientation == 'horizontal'
    },
    isVertical() {
      return this.$props.orientation == 'vertical'
    }
  },
  data() {
    return {
      context: null,
      requiresRender: true
    }
  },
  watch: {
    visible: function(newVal) {
      this.renderRuler()
    },
    theme: function() {
      this.renderRuler()
    },
    scale: function() {
      if(this.requiresRender)
        this.renderRuler()
    },
    mousePosition: function() {
      if(this.requiresRender)
        this.renderRuler()
    },
    originOffset: function() {
      if(this.requiresRender)
        this.renderRuler()
    }
  },
  mounted() {
    this.$data.context = this.$refs['rulerCanvas'].getContext('2d')
    this.renderRuler()
  },
  methods: {
    renderRuler() {
      if(!this.context || !this.visible)
        return

      this.requiresRender = false

      var bgColour = '#ffffff'
      var tickColour = '#606060'
      var labelColour = '#202020'
      var mouseColour = '#40ef40'

      if(this.theme) {
        bgColour = this.theme.rulerBackground
        tickColour = this.theme.rulerTickColour
        labelColour = this.theme.rulerLabelColour
        mouseColour = this.theme.rulerMouseColour
      }
      
      const ctr = this.$refs['canvasContainer']
      const canvas = this.$refs['rulerCanvas']
      const ctx = this.$data.context
      const ratio = Math.max(window.devicePixelRatio || 1, 1)

      // Setup & clear it all
      canvas.width = ctr.clientWidth * ratio
      canvas.height = ctr.clientHeight * ratio

      ctx.fillStyle = bgColour
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      ctx.lineWidth = 1
      ctx.strokeStyle = tickColour
      ctx.fillStyle = labelColour
      ctx.font = '11px Arial'
      ctx.textAlign = 'left'

      let rulerScale = this.$props.scale

      // Add in the ticks & timestamps
      var smallTickLength = 5
      var midTickLength = this.isHorizontal ? Math.floor(canvas.height * 0.4) : Math.floor(canvas.width * 0.4)
      var labelTickLength = this.isHorizontal ? Math.floor(canvas.height * 0.66) : Math.floor(canvas.width * 0.66)
      let rulerDistance = this.isHorizontal ? canvas.width : canvas.height
      let origin = this.$props.originOffset * ratio
      let tickDistance = rulerDistance + Math.abs(origin)

      var labelRate = 10 * rulerScale
      var largeTickCount = 5
      var labelTickCount = 5

      var scaleMap = [6, 5, 3.5, 2, 1.5, 1.0, 0.7, 0.4, 0.2]
      var labelMap = [5, 5, 10,  25,50,  100, 250, 500, 1000]
      var labelTickMap = [5, 10, 10, 5, 5, 5, 5, 5, 5]     // Note: Needs to be divisible by largeTickCount

      labelRate = labelMap[0] * rulerScale
      labelTickCount = labelTickMap[0]

      // Calculate an appropriate label rate that is rounded
      scaleMap.forEach(function(scale, index) {
        if(rulerScale <= scale) {
          labelRate = labelMap[index] * rulerScale
          labelTickCount = labelTickMap[index]
        }
      })

      var tickRate = labelRate / largeTickCount
      
      // Ticks
      var tickIndex = 0, t = 0
      for(t=0; t<tickDistance; t += tickRate, tickIndex++) {
        var tickLength = smallTickLength
        if(tickIndex % largeTickCount == 0) {
          tickLength = midTickLength
          if(tickIndex % labelTickCount == 0)
            tickLength = labelTickLength
        }

        if(tickIndex == 0)
          tickLength = labelTickLength

        ctx.beginPath()
        if(this.isHorizontal) {
          if(origin + t <= rulerDistance) {
            ctx.moveTo(Math.ceil(origin + t)+0.5, canvas.height)
            ctx.lineTo(Math.ceil(origin + t)+0.5, canvas.height-tickLength)
          }
          if(origin - t >= 0 && tickIndex > 0) {
            ctx.moveTo(Math.ceil(origin - t)+0.5, canvas.height)
            ctx.lineTo(Math.ceil(origin - t)+0.5, canvas.height-tickLength)
          }
        } else {
          if(origin + t <= rulerDistance) {
            ctx.moveTo(canvas.width, Math.ceil(origin + t)-0.5)
            ctx.lineTo(canvas.width-tickLength, Math.ceil(origin + t)-0.5)
          }
          if(origin - t >= 0 && tickIndex > 0) {
            ctx.moveTo(canvas.width, Math.ceil(origin - t)-0.5)
            ctx.lineTo(canvas.width-tickLength, Math.ceil(origin - t)-0.5)
          }
        }
        ctx.stroke()
      }

      // Mouse position
      if(this.showMousePosition) {
        let position = (this.$props.mousePosition * ratio) * rulerScale + origin
        ctx.strokeStyle = mouseColour
        ctx.beginPath()
        if(this.isHorizontal) {
          ctx.moveTo(Math.ceil(position)+0.5, canvas.height)
          ctx.lineTo(Math.ceil(position)+0.5, 0)
        } else {
          ctx.moveTo(canvas.width, Math.ceil(position)-0.5)
          ctx.lineTo(0, Math.ceil(position)-0.5)
        }
        ctx.stroke()
      }

      // Labels
      ctx.save()
      if(this.isVertical) {
        ctx.rotate(-Math.PI/2)
        ctx.textAlign = 'right'
      }

      let rulerSize = 20

      tickIndex = 0
      for(t=0; t<tickDistance; t += tickRate, tickIndex++) {
        // Labels
        if(tickIndex % labelTickCount == 0) {
          var timeLabel = ('' + Math.round(t / rulerScale))

          if(this.isHorizontal) {
            if(origin + t <= rulerDistance)
              ctx.fillText(timeLabel, Math.ceil(origin + t)+0.5 + 3, rulerSize/2);
            if(origin - t >= 0 && tickIndex > 0)
              ctx.fillText('-' + timeLabel, Math.ceil(origin - t)+0.5 + 3, rulerSize/2);
          } else {
            if(origin + t <= rulerDistance)
              ctx.fillText(timeLabel, -Math.ceil(origin + t)+0.5 - 3, rulerSize/2);
            if(origin - t >= 0 && tickIndex > 0)
              ctx.fillText('-' + timeLabel, -Math.ceil(origin - t)+0.5 - 3, rulerSize/2);
          }
        }
      }
      ctx.restore()
    },
    setRequiresRender() {
      this.requiresRender = true
    }
  }
}
</script>